import { useState } from "react";
import { PlusSmallIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Switch } from "@headlessui/react";

import { IPostcodeYearRule } from "../../models/PostcodeGroup";
import usePostcodeYearRules from "../../data/usePostcodeYearRules";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification, { NotificationVariant } from "../notifications/useNotifications";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";

interface PostcodeYearRulesProps {
  /** The price per tonne the rules move, i.e. the group's base price or the area's price. */
  basePrice: number;
  yearRules: IPostcodeYearRule[];
  /** Exactly one of these — the rule hangs off a group or off one of its areas. */
  postcodeGroupId?: string;
  postcodePriceId?: string;
  /** How the owner reads on screen, e.g. "group DN" or "area DN1". */
  ownerLabel: string;
  /** Areas sit inside an already-tight table row, so they get the terser layout. */
  compact?: boolean;
}

/** "" for a cleared box means unbounded, which is not the same as 0. */
function toYearValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function describeYears(rule: { fromYear: number | null; toYear: number | null }) {
  if (rule.fromYear === null && rule.toYear === null) return "Any year";
  if (rule.toYear === null) return `${rule.fromYear} and newer`;
  if (rule.fromYear === null) return `${rule.toYear} and older`;
  return `${rule.fromYear} to ${rule.toYear}`;
}

export default function PostcodeYearRules({
  basePrice,
  yearRules,
  postcodeGroupId,
  postcodePriceId,
  ownerLabel,
  compact = false,
}: PostcodeYearRulesProps) {
  const { create, update, remove } = usePostcodeYearRules();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Year Rule");
  const { addNotification } = useNotification();

  const [fromYear, setFromYear] = useState("");
  const [toYear, setToYear] = useState("");
  const [pricePerTonne, setPricePerTonne] = useState(basePrice.toString());

  const reject = (message: string) =>
    addNotification({
      variant: NotificationVariant.error,
      primaryText: "Validation errors",
      secondaryText: message,
    });

  const handleAdd = () => {
    const from = toYearValue(fromYear);
    const to = toYearValue(toYear);
    const price = parseFloat(pricePerTonne);

    if (from === null && to === null) return reject("Set a from year, a to year, or both.");
    if (from !== null && to !== null && from > to) return reject("The from year can't be after the to year.");
    if (Number.isNaN(price) || price < 0) return reject("Enter a price per tonne of 0 or more.");

    create.mutate(
      { postcodeGroupId, postcodePriceId, fromYear: from, toYear: to, pricePerTonne: price, isActive: true },
      {
        onSuccess: () => {
          setFromYear("");
          setToYear("");
          setPricePerTonne(basePrice.toString());
          saveHandlers.onSuccess();
        },
        onError: saveHandlers.onError,
      }
    );
  };

  const handleUpdate = (rule: IPostcodeYearRule, changes: Partial<IPostcodeYearRule>) => {
    const next = { ...rule, ...changes };

    if (next.fromYear === null && next.toYear === null) return reject("Set a from year, a to year, or both.");
    if (next.fromYear !== null && next.toYear !== null && next.fromYear > next.toYear)
      return reject("The from year can't be after the to year.");
    if (Number.isNaN(next.pricePerTonne) || next.pricePerTonne < 0)
      return reject("Enter a price per tonne of 0 or more.");

    update.mutate(
      {
        id: next.id,
        fromYear: next.fromYear,
        toYear: next.toYear,
        pricePerTonne: next.pricePerTonne,
        isActive: next.isActive,
      },
      { ...saveHandlers }
    );
  };

  const sorted = [...yearRules].sort(
    (a, b) => (a.fromYear ?? -Infinity) - (b.fromYear ?? -Infinity)
  );

  return (
    <div className="space-y-4">
      {!compact && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Year Rules</h2>
          <small className="text-gray-500">
            <i>
              Change the price per tonne for the {ownerLabel} based on how old the vehicle is.
              Everything outside these bands stays on {PriceFormat(basePrice)} per tonne.
            </i>
          </small>
        </div>
      )}

      {/* The two things that catch people out, said once rather than left to be discovered. */}
      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-1">
        <p>
          <span className="font-semibold">Years are inclusive.</span> For "newer than 2015" put 2016
          in From, and for "older than 2010" put 2009 in To. Leave a box empty for no limit.
        </p>
        <p>
          Only one rule is ever used — it replaces the price per tonne rather than adding to it. When
          two bands both fit, the narrower one wins, then the higher price.
        </p>
        {postcodeGroupId && (
          <p>
            Rules on a group are ignored for any postcode that has its own area price. Put the rule
            on the area to change that.
          </p>
        )}
      </div>

      {/* Add */}
      <div className={classNames("grid gap-3", compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-4")}>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">From Year</label>
          <input
            type="number"
            value={fromYear}
            onChange={(e) => setFromYear(e.target.value)}
            placeholder="No limit"
            className="w-full text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1.5"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">To Year</label>
          <input
            type="number"
            value={toYear}
            onChange={(e) => setToYear(e.target.value)}
            placeholder="No limit"
            className="w-full text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1.5"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Price per Tonne (£)</label>
          <input
            type="number"
            step="0.01"
            value={pricePerTonne}
            onChange={(e) => setPricePerTonne(e.target.value)}
            className="w-full text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1.5"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={handleAdd}
            disabled={create.isLoading}
            className="w-full inline-flex items-center justify-center rounded-md bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 px-3 py-1.5 text-sm font-medium text-white shadow-sm"
          >
            <PlusSmallIcon className="h-4 w-4 mr-1" />
            Add Rule
          </button>
        </div>
      </div>

      {/* Existing */}
      {sorted.length === 0 ? (
        <p className="text-sm text-gray-500">
          No year rules yet — every vehicle in this {ownerLabel} is priced at {PriceFormat(basePrice)} per tonne.
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map((rule) => (
            <div
              key={rule.id}
              className={classNames(
                "rounded-lg border p-3",
                rule.isActive ? "bg-white border-gray-200" : "bg-gray-50 border-gray-200"
              )}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <span
                  className={classNames(
                    "text-sm font-semibold",
                    rule.isActive ? "text-gray-800" : "text-gray-500"
                  )}
                >
                  {describeYears(rule)} &rarr; {PriceFormat(rule.pricePerTonne)} per tonne
                </span>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={rule.isActive}
                    onChange={(isActive) => handleUpdate(rule, { isActive })}
                    className={classNames(
                      rule.isActive ? "bg-primary-600" : "bg-gray-200",
                      "relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors"
                    )}
                  >
                    <span className="sr-only">Active</span>
                    <span
                      className={classNames(
                        rule.isActive ? "translate-x-4" : "translate-x-0",
                        "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition"
                      )}
                    />
                  </Switch>
                  <button
                    type="button"
                    onClick={() => remove.mutate(rule.id, { ...deleteHandlers })}
                    className="text-gray-400 hover:text-red-600"
                    title="Delete rule"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From Year</label>
                  <input
                    type="number"
                    defaultValue={rule.fromYear ?? ""}
                    placeholder="No limit"
                    onBlur={(e) => {
                      const value = toYearValue(e.target.value);
                      if (value !== rule.fromYear) handleUpdate(rule, { fromYear: value });
                    }}
                    className="w-full text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To Year</label>
                  <input
                    type="number"
                    defaultValue={rule.toYear ?? ""}
                    placeholder="No limit"
                    onBlur={(e) => {
                      const value = toYearValue(e.target.value);
                      if (value !== rule.toYear) handleUpdate(rule, { toYear: value });
                    }}
                    className="w-full text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price per Tonne (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    defaultValue={rule.pricePerTonne}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value);
                      if (value !== rule.pricePerTonne) handleUpdate(rule, { pricePerTonne: value });
                    }}
                    className="w-full text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
